import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Users, Globe, Award } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Home() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/4010636/pexels-photo-4010636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Academic campus"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-cabernet/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-24 sm:py-32 lg:py-40">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-[0.2em] font-accent font-bold text-brass mb-4 animate-fade-in">
              501(c)(3) Non-Profit Organization
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium text-cream tracking-tight leading-none mb-6 animate-fade-in-delay-1">
              Empowering Future<br />Global Entrepreneurs
            </h1>
            <p className="text-base sm:text-lg font-body text-cream/85 leading-relaxed mb-8 max-w-xl animate-fade-in-delay-2">
              We provide scholarships and mentorship to high school students passionate 
              about real estate, business, and international entrepreneurship.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-delay-3">
              <Link to="/scholarships">
                <Button
                  className="bg-brass text-ink hover:bg-brass/90 font-accent text-sm px-6 py-5 rounded-md"
                  data-testid="hero-apply-btn"
                >
                  Apply for Scholarship
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/donate">
                <Button
                  variant="outline"
                  className="border-2 border-cream/50 text-cream hover:bg-cream/10 font-accent text-sm px-6 py-5 rounded-md"
                  data-testid="hero-donate-btn"
                >
                  Support Our Mission
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-16 sm:py-20 bg-cream" data-testid="impact-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-heading font-medium text-cabernet tracking-tight">
              2027 Goals
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {[
              { number: '$20,000+', label: 'Scholarships Awarded', icon: Award },
              { number: '20+', label: 'Students Impacted', icon: Users },
              { number: '12', label: 'Mentorship Programs', icon: BookOpen },
              { number: '5', label: 'Countries Reached', icon: Globe },
            ].map((stat) => (
              <div key={stat.label} className="text-center p-6 bg-card-surface rounded-lg border border-brass/20 shadow-sm">
                <stat.icon className="w-6 h-6 text-brass mx-auto mb-3" />
                <p className="text-2xl sm:text-3xl font-heading font-semibold text-cabernet">{stat.number}</p>
                <p className="text-sm font-accent text-ink/60 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Preview */}
      <section className="py-16 sm:py-20 bg-card-surface" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] font-accent font-bold text-brass mb-3">Our Mission</p>
              <h2 className="text-3xl sm:text-4xl font-heading font-medium text-cabernet tracking-tight leading-snug mb-5">
                Building Tomorrow's Leaders Through Global Exposure
              </h2>
              <p className="font-body text-base text-ink/80 leading-relaxed mb-6">
                The Global Visionaries Scholarship provides high school students with firsthand 
                exposure to international markets and development projects, accelerating their 
                professional growth in real estate, business, and entrepreneurship.
              </p>
              <Link to="/about">
                <Button
                  variant="outline"
                  className="border-2 border-cabernet text-cabernet hover:bg-cabernet/5 font-accent text-sm"
                  data-testid="learn-more-btn"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="rounded-lg overflow-hidden shadow-md border border-brass/20">
              <img
                src="https://images.unsplash.com/photo-1758525860449-fa3602fceb31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwaGlnaCUyMHNjaG9vbCUyMHN0dWRlbnRzJTIwc3R1ZHlpbmclMjB0b2dldGhlcnxlbnwwfHx8fDE3Nzc1MDY1Njl8MA&ixlib=rb-4.1.0&q=85"
                alt="Students collaborating"
                className="w-full h-72 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-cabernet" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-medium text-cream tracking-tight mb-4">
            Ready to Shape Your Future?
          </h2>
          <p className="font-body text-base text-cream/75 mb-8 max-w-2xl mx-auto">
            Whether you're a student seeking opportunity or a donor looking to make an impact, 
            join the Global Visionaries community today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/scholarships">
              <Button
                className="bg-brass text-ink hover:bg-brass/90 font-accent text-sm px-6 py-5"
                data-testid="cta-apply-btn"
              >
                View Scholarships
              </Button>
            </Link>
            <Link to="/mentorship">
              <Button
                variant="outline"
                className="border-2 border-cream/50 text-cream hover:bg-cream/10 font-accent text-sm px-6 py-5"
                data-testid="cta-mentorship-btn"
              >
                Explore Mentorship
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
